import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { usePermissions } from "../../hooks/usePermissions";
import { setUser } from "../../store/slices/authSlice";
import { useFormik } from "formik";
import * as Yup from "yup";
import { DATE_DISPLAY_FORMAT, toApiDate } from "../../utils/dateUtils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Edit,
  Save,
  Cancel,
  PhotoCamera,
  Email,
  Phone,
  LocationOn,
  School,
  Business,
  Person,
  Lock,
  Visibility,
  VisibilityOff,
  SupervisorAccount,
  DeleteOutline,
  Notifications,
} from "@mui/icons-material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { useUpdateProfile as useUpdateUserProfile, useChangePassword } from "../../hooks/queries/useProfile";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import profileService from "../../services/profileService";
import Toast from "../../components/common/Toast";
import ConfirmDialog from "../../components/forms/ConfirmDialog";
import { getAvatarInitial } from "../../utils/avatarUtils";
import { useNavigate } from "react-router-dom";
import {
  requiredTextFieldProps,
  requiredFormControlLabelProps,
  requiredInputLabelProps,
  REQUIRED_ASTERISK_SX,
} from "../../utils/requiredFieldProps";

// Indian states and state-to-cities for institute profile (dropdown when role = institute)
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya",
  "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal", "Chandigarh", "Delhi", "Jammu and Kashmir", "Ladakh", "Puducherry"
].sort();

const STATE_CITIES = {
  "Andhra Pradesh": ["Anantapur", "Chittoor", "Guntur", "Kakinada", "Kurnool", "Nellore", "Rajahmundry", "Tirupati", "Vijayawada", "Visakhapatnam"].sort(),
  "Arunachal Pradesh": ["Itanagar", "Naharlagun"].sort(),
  "Assam": ["Dibrugarh", "Guwahati", "Silchar", "Jorhat"].sort(),
  "Bihar": ["Bhagalpur", "Gaya", "Muzaffarpur", "Patna"].sort(),
  "Chhattisgarh": ["Bhilai", "Bilaspur", "Raipur"].sort(),
  "Goa": ["Panaji", "Margao", "Vasco da Gama"].sort(),
  "Gujarat": ["Ahmedabad", "Anand", "Bhavnagar", "Gandhinagar", "Jamnagar", "Junagadh", "Rajkot", "Surat", "Vadodara"].sort(),
  "Haryana": ["Ambala", "Faridabad", "Gurugram", "Hisar", "Karnal", "Panipat", "Rohtak"].sort(),
  "Himachal Pradesh": ["Shimla", "Dharamshala", "Solan", "Mandi"].sort(),
  "Jharkhand": ["Bokaro", "Dhanbad", "Jamshedpur", "Ranchi"].sort(),
  "Karnataka": ["Bengaluru", "Belgaum", "Bellary", "Hubli", "Mangalore", "Mysore"].sort(),
  "Kerala": ["Kannur", "Kochi", "Kollam", "Kozhikode", "Thiruvananthapuram", "Thrissur"].sort(),
  "Madhya Pradesh": ["Bhopal", "Gwalior", "Indore", "Jabalpur", "Ujjain"].sort(),
  "Maharashtra": ["Aurangabad", "Kolhapur", "Mumbai", "Nagpur", "Nashik", "Pune", "Sangli", "Thane"].sort(),
  "Manipur": ["Imphal"].sort(),
  "Meghalaya": ["Shillong"].sort(),
  "Mizoram": ["Aizawl"].sort(),
  "Nagaland": ["Dimapur", "Kohima"].sort(),
  "Odisha": ["Bhubaneswar", "Cuttack", "Rourkela"].sort(),
  "Punjab": ["Amritsar", "Bathinda", "Jalandhar", "Ludhiana", "Patiala"].sort(),
  "Rajasthan": ["Ajmer", "Alwar", "Bharatpur", "Bikaner", "Jaipur", "Jodhpur", "Kota", "Udaipur"].sort(),
  "Sikkim": ["Gangtok"].sort(),
  "Tamil Nadu": ["Chennai", "Coimbatore", "Madurai", "Salem", "Tiruchirappalli", "Tirunelveli"].sort(),
  "Telangana": ["Hyderabad", "Warangal", "Nizamabad"].sort(),
  "Tripura": ["Agartala"].sort(),
  "Uttar Pradesh": ["Agra", "Aligarh", "Allahabad", "Bareilly", "Firozabad", "Ghaziabad", "Gorakhpur", "Jhansi", "Kanpur", "Lucknow", "Mathura", "Meerut", "Noida", "Varanasi"].sort(),
  "Uttarakhand": ["Dehradun", "Haridwar", "Roorkee"].sort(),
  "West Bengal": ["Asansol", "Durgapur", "Howrah", "Kolkata", "Siliguri"].sort(),
  "Chandigarh": ["Chandigarh"].sort(),
  "Delhi": ["Delhi", "New Delhi"].sort(),
  "Jammu and Kashmir": ["Jammu", "Srinagar"].sort(),
  "Ladakh": ["Leh"].sort(),
  "Puducherry": ["Puducherry"].sort(),
};

const validationSchema = Yup.object().shape({
  // Common fields
  firstName: Yup.string()
    .when('$isInstitute', {
      is: false,
      then: (schema) => schema
        .required('First name is required')
        .matches(/^[a-zA-Z\s]*$/, 'First name can only contain letters and spaces')
        .test('no-numbers', 'First name cannot contain numbers', value => {
          return !/\d/.test(value);
        })
        .test('no-special-chars', 'First name cannot contain special characters', value => {
          return /^[a-zA-Z\s]+$/.test(value);
        }),
      otherwise: (schema) => schema.notRequired()
    }),
  lastName: Yup.string()
    .when('$isInstitute', {
      is: false,
      then: (schema) => schema
        .required('Last name is required')
        .matches(/^[a-zA-Z\s]*$/, 'Last name can only contain letters and spaces')
        .test('no-numbers', 'Last name cannot contain numbers', value => {
          return !/\d/.test(value);
        })
        .test('no-special-chars', 'Last name cannot contain special characters', value => {
          return /^[a-zA-Z\s]+$/.test(value);
        }),
      otherwise: (schema) => schema.notRequired()
    }),
  gender: Yup.string()
    .when(['$isSuperAdmin', '$isTeacherOrHOD'], {
      is: (isSuperAdmin, isTeacherOrHOD) => isSuperAdmin || isTeacherOrHOD,
      then: (schema) => schema.required('Gender is required'),
      otherwise: (schema) => schema.notRequired()
    }),
  dob: Yup.date()
    .nullable()
    .when(['$isSuperAdmin', '$isTeacherOrHOD'], {
      is: (isSuperAdmin, isTeacherOrHOD) => isSuperAdmin || isTeacherOrHOD,
      then: (schema) =>
        schema
          .test('dob-required', 'Date of birth is required', function (value) {
            const v = this.originalValue ?? value;
            if (v === undefined || v === null || (typeof v === 'string' && !v.trim())) return false;
            return true;
          })
          .required('Date of birth is required')
          .typeError('Please enter a valid date (YYYY-MM-DD)')
          .max(new Date(), 'Date of birth cannot be in the future')
          .test('valid-date', 'Please enter a valid date', function (value) {
            if (!value) return false;
            const d = new Date(value);
            return !isNaN(d.getTime());
          })
          .test('year-4-digits', 'Year must be 4 digits (YYYY). Please enter a valid date.', function (value) {
            if (!value) return false;
            const year = new Date(value).getFullYear();
            return year >= 1000 && year <= 9999;
          })
          .test('min-age-16', 'Must be at least 16 years old', function (value) {
            if (!value) return false;
            const d = new Date(value);
            const currentYear = new Date().getFullYear();
            const birthYear = d.getFullYear();
            return birthYear <= currentYear - 16;
          })
          .test('max-age-100', 'Please enter a valid birth year', function (value) {
            if (!value) return false;
            const currentYear = new Date().getFullYear();
            const birthYear = new Date(value).getFullYear();
            return birthYear >= currentYear - 100;
          }),
      otherwise: (schema) => schema.notRequired()
    }),
  name: Yup.string()
    .when('$isInstituteOrAdmin', {
      is: true,
      then: (schema) => schema.required('Institute name is required'),
      otherwise: (schema) => schema.notRequired()
    }),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  phone: Yup.string()
    .test('phone', 'Invalid phone number', function(value) {
      if (!value) return false;
      // Allow any valid international phone number format
      return value.replace(/\D/g, '').length >= 10;
    })
    .required('Phone is required'),
  
  // Institute / Admin / Teacher-HOD required contact fields
  street: Yup.string()
    .when(['$isInstituteOrAdmin', '$isTeacherOrHOD'], {
      is: (isInstituteOrAdmin, isTeacherOrHOD) => isInstituteOrAdmin || isTeacherOrHOD,
      then: (schema) => schema.required('Street is required'),
      otherwise: (schema) => schema.notRequired()
    }),
  city: Yup.string()
    .when(['$isInstituteOrAdmin', '$isTeacherOrHOD'], {
      is: (isInstituteOrAdmin, isTeacherOrHOD) => isInstituteOrAdmin || isTeacherOrHOD,
      then: (schema) => schema.required('City is required'),
      otherwise: (schema) => schema.notRequired()
    }),
  state: Yup.string()
    .when(['$isInstituteOrAdmin', '$isTeacherOrHOD'], {
      is: (isInstituteOrAdmin, isTeacherOrHOD) => isInstituteOrAdmin || isTeacherOrHOD,
      then: (schema) => schema.required('State is required'),
      otherwise: (schema) => schema.notRequired()
    }),
  country: Yup.string()
    .when(['$isInstituteOrAdmin', '$isTeacherOrHOD'], {
      is: (isInstituteOrAdmin, isTeacherOrHOD) => isInstituteOrAdmin || isTeacherOrHOD,
      then: (schema) => schema.required('Country is required'),
      otherwise: (schema) => schema.notRequired()
    }),
  pinCode: Yup.string()
    .when(['$isInstituteOrAdmin', '$isTeacherOrHOD'], {
      is: (isInstituteOrAdmin, isTeacherOrHOD) => isInstituteOrAdmin || isTeacherOrHOD,
      then: (schema) => schema
        .matches(/^[0-9]{3,9}$/, 'Pincode must be 3-9 digits')
        .required('Pincode is required'),
      otherwise: (schema) => schema.notRequired()
    }),
  dobRaw: Yup.string()
    .when(['$isSuperAdmin', '$isTeacherOrHOD'], {
      is: (isSuperAdmin, isTeacherOrHOD) => isSuperAdmin || isTeacherOrHOD,
      then: (schema) =>
        schema
          .required('Date of birth is required')
          .test('valid-dob-format', 'Please enter a valid date (e.g. YYYY-MM-DD)', function (value) {
            if (!value || !value.trim()) return false;
            const trimmed = value.trim();
            if (/mm|dd|m\/d|d\/m/i.test(trimmed)) return false;
            const d = new Date(trimmed);
            if (isNaN(d.getTime())) return false;
            const year = d.getFullYear();
            if (year < 1000 || year > 9999) return false;
            if (year > new Date().getFullYear()) return false;
            const currentYear = new Date().getFullYear();
            if (year < currentYear - 100) return false;
            if (year > currentYear - 16) return false;
            return true;
          }),
      otherwise: (schema) => schema.notRequired()
    }),
  website: Yup.string()
    .when('$isInstituteOrAdmin', {
      is: true,
      then: (schema) => schema
        .url('Must be a valid URL')
        .required('Website is required'),
      otherwise: (schema) => schema.notRequired()
    }),
  qualification: Yup.string()
    .when('$isTeacherOrHOD', {
      is: true,
      then: (schema) => schema
        .required('Qualification is required')
        .max(120, 'Qualification must be at most 120 characters'),
      otherwise: (schema) => schema.notRequired()
    }),
  experienceYears: Yup.number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null || originalValue === undefined ? NaN : Number(originalValue)
    )
    .when('$isTeacherOrHOD', {
      is: true,
      then: (schema) => schema
        .typeError('Experience must be a valid number')
        .required('Experience is required')
        .min(0, 'Experience cannot be negative')
        .max(60, 'Experience cannot exceed 60 years'),
      otherwise: (schema) => schema.notRequired()
    }),
  joiningDate: Yup.date()
    .nullable()
    .when('$isTeacherOrHOD', {
      is: true,
      then: (schema) => schema
        .required('Joining date is required')
        .typeError('Please enter a valid joining date')
        .max(new Date(), 'Joining date cannot be in the future'),
      otherwise: (schema) => schema.notRequired()
    }),
  designation: Yup.string()
    .when('$isTeacherOrHOD', {
      is: true,
      then: (schema) => schema
        .required('Designation is required')
        .max(120, 'Designation must be at most 120 characters'),
      otherwise: (schema) => schema.notRequired()
    }),
  departmentName: Yup.string()
    .when('$isTeacherOrHOD', {
      is: true,
      then: (schema) => schema
        .required('Department name is required')
        .max(150, 'Department name must be at most 150 characters'),
      otherwise: (schema) => schema.notRequired()
    }),
  salary: Yup.string()
    .when('$isTeacherOrHOD', {
      is: true,
      then: (schema) => schema
        .required('Salary is required')
        .matches(/^\d+(\.\d{1,2})?$/, 'Salary must be a valid number (up to 2 decimals)'),
      otherwise: (schema) => schema.notRequired()
    }),
  // QR/OTP token refresh interval (seconds). Optional integer 15..1800.
  // Empty/blank -> undefined (not required) so the system default (60s) applies.
  attendanceTokenRefreshSeconds: Yup.number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null || originalValue === undefined
        ? undefined
        : Number(originalValue)
    )
    .typeError('Refresh interval must be a valid number')
    .integer('Refresh interval must be a whole number of seconds')
    .min(15, 'Refresh interval must be at least 15 seconds')
    .max(1800, 'Refresh interval cannot exceed 1800 seconds')
    .notRequired(),
});

// Profile photo validation rules — applied for every role (admin, institute, superadmin, teacher, hod)
const PROFILE_PHOTO_RULES = {
  allowedMimeTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'],
  allowedExtensions: ['.jpeg', '.jpg', '.png', '.gif'],
  maxSizeBytes: 5 * 1024 * 1024, // 5MB
  minSizeBytes: 100, // avoid empty/corrupt files
  minWidth: 100,
  minHeight: 100,
  maxWidth: 2000,
  maxHeight: 2000,
};

/**
 * Validates profile photo file for all roles. Returns { valid: boolean, error?: string }.
 */
const validateProfilePhoto = (file) => {
  if (!file || !(file instanceof File)) {
    return { valid: false, error: 'Please select a file.' };
  }
  const ext = file.name ? file.name.toLowerCase().slice(file.name.lastIndexOf('.')) : '';
  if (!PROFILE_PHOTO_RULES.allowedExtensions.includes(ext)) {
    return { valid: false, error: 'Invalid file type. Allowed: JPEG, PNG, or GIF.' };
  }
  if (!PROFILE_PHOTO_RULES.allowedMimeTypes.includes(file.type)) {
    return { valid: false, error: 'Invalid file type. Please select a valid image (JPEG, PNG, or GIF).' };
  }
  if (file.size < PROFILE_PHOTO_RULES.minSizeBytes) {
    return { valid: false, error: 'File is too small or empty. Please choose a valid image.' };
  }
  if (file.size > PROFILE_PHOTO_RULES.maxSizeBytes) {
    return { valid: false, error: 'Image size must be less than 5MB.' };
  }
  return { valid: true };
};

/**
 * Validates image dimensions by loading the file. Returns a Promise<{ valid: boolean, error?: string }>.
 */
const validateProfilePhotoDimensions = (file) => {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    const cleanup = () => URL.revokeObjectURL(url);

    img.onload = () => {
      cleanup();
      const { minWidth, minHeight, maxWidth, maxHeight } = PROFILE_PHOTO_RULES;
      if (img.width < minWidth || img.height < minHeight) {
        resolve({ valid: false, error: `Image must be at least ${minWidth}×${minHeight} pixels.` });
        return;
      }
      if (img.width > maxWidth || img.height > maxHeight) {
        resolve({ valid: false, error: `Image must not exceed ${maxWidth}×${maxHeight} pixels.` });
        return;
      }
      resolve({ valid: true });
    };

    img.onerror = () => {
      cleanup();
      resolve({ valid: false, error: 'File could not be read as an image. Please choose a valid image file.' });
    };

    img.src = url;
  });
};

const PASSWORD_RULES = {
  min: 8,
  capital: /[A-Z]/,
  small: /[a-z]/,
  number: /[0-9]/,
  special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
};

const passwordSchema = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .required("New password is required")
    .min(PASSWORD_RULES.min, "Password must be at least 8 characters")
    .matches(PASSWORD_RULES.capital, "Must contain at least one capital letter")
    .matches(PASSWORD_RULES.small, "Must contain at least one small letter")
    .matches(PASSWORD_RULES.number, "Must contain at least one number")
    .matches(PASSWORD_RULES.special, "Must contain at least one special character")
    .test(
      "not-old",
      "New password must be different from current password",
      function (value) {
        return !value || value !== this.parent.currentPassword;
      }
    ),
  confirmNewPassword: Yup.string()
    .required("Confirm new password is required")
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .min(PASSWORD_RULES.min, "Password must be at least 8 characters")
    .matches(PASSWORD_RULES.capital, "Must contain at least one capital letter")
    .matches(PASSWORD_RULES.small, "Must contain at least one small letter")
    .matches(PASSWORD_RULES.number, "Must contain at least one number")
    .matches(PASSWORD_RULES.special, "Must contain at least one special character"),
});

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const isInstitute = user?.role?.toLowerCase() === 'institute';
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const isSuperAdmin = user?.role?.toLowerCase() === 'superadmin';
  const { selectedInstitute, isInInstituteContext } = useSelector((state) => state.instituteContext);
  const userRole = (typeof (user?.role?.slug || user?.role) === "string" ? (user?.role?.slug || user?.role).toLowerCase() : user?.role);
  const normalizedRole = userRole === "institute" ? "admin" : userRole;
  const isSuperAdminInstituteMode =
    isSuperAdmin &&
    Boolean(
      (isInInstituteContext && selectedInstitute) ||
        user?.operatingInstituteId ||
        user?.isManagingInstitute
    );
  const isSuperAdminPersonalMode = isSuperAdmin && !isSuperAdminInstituteMode;
  const showInstituteProfile =
    isSuperAdminInstituteMode ||
    normalizedRole === "admin" ||
    isInstitute;
  const updateProfileMutation = useUpdateUserProfile();
  const changePasswordMutation = useChangePassword();
  const [activeTab, setActiveTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [success, setSuccess] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState("success");
  const [removePhotoConfirmOpen, setRemovePhotoConfirmOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [avatarImageError, setAvatarImageError] = useState(false);

  // Compact contact info (authoritative view) from GET /profile/contact
  const {
    data: profileContactResponse,
    isLoading: isContactLoading,
  } = useQuery({
    queryKey: ["profileContact"],
    queryFn: () => profileService.getProfileContact(),
    refetchOnMount: "always",
    staleTime: 0,
    gcTime: 0,
  });

  const queryClient = useQueryClient();

  // Notification preferences (push toggle)
  const {
    data: notificationSettingsResponse,
    isLoading: isNotificationSettingsLoading,
  } = useQuery({
    queryKey: ["notificationSettings"],
    queryFn: () => profileService.getNotificationSettings(),
    refetchOnMount: "always",
    staleTime: 0,
    gcTime: 0,
  });

  const pushNotificationsEnabled =
    (notificationSettingsResponse?.data?.data?.pushNotificationsEnabled ??
      notificationSettingsResponse?.data?.pushNotificationsEnabled ??
      user?.pushNotificationsEnabled) !== false;

  const updateNotificationSettingsMutation = useMutation({
    mutationFn: (payload) => profileService.updateNotificationSettings(payload),
    onSuccess: (res, variables) => {
      const enabled =
        (res?.data?.data?.pushNotificationsEnabled ??
          res?.data?.pushNotificationsEnabled ??
          variables?.pushNotificationsEnabled) !== false;
      const updatedUser = { ...user, pushNotificationsEnabled: enabled };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      dispatch(setUser(updatedUser));
      setToastMessage("Notification preference updated");
      setToastSeverity("success");
    },
    onError: (error) => {
      setToastMessage(error?.response?.data?.message || "Failed to update notification preference");
      setToastSeverity("error");
    },
  });

  const updateInstituteDetails = useMutation({
    mutationFn: (data) => {
      const payload = {
        name: data.name,
        street: data.street,
        city: data.city,
        state: data.state,
        country: data.country,
        pinCode: data.pinCode,
        website: data.website,
        phoneNumber: data.phoneNumber || data.phone,
        latitude: data.latitude,
        longitude: data.longitude,
        instituteDistanceRadius: data.instituteDistanceRadius,
        teacherClassDistanceRadius: data.teacherClassDistanceRadius,
        // Raw value; backend parseTokenRefreshSecondsForStore treats "" as null (system default 60s)
        attendanceTokenRefreshSeconds: data.attendanceTokenRefreshSeconds
      };
      return profileService.updateInstituteDetails(payload);
    },
    onSuccess: async (response) => {
      // Invalidate and refetch immediately so GET profile data is fresh
      await queryClient.invalidateQueries({ queryKey: ['user'] });
      await queryClient.invalidateQueries({ queryKey: ["instituteProfileDetails"] });
      await queryClient.refetchQueries({ queryKey: ["instituteProfileDetails"] });

      const updatedUser = await queryClient.getQueryData(['user']);
      const instituteData = await queryClient.getQueryData(["instituteProfileDetails"]);
      const raw = instituteData?.data?.data ?? instituteData?.data?.user ?? instituteData?.data ?? instituteData ?? {};
      const formUser = updatedUser || raw;
      formik.resetForm({
        values: {
          ...formik.values,
          ...formUser,
          pinCode: formUser.pinCode ?? formUser.pincode ?? "",
          phone: formUser.phoneNumber ?? formUser.phone ?? ""
        }
      });

      setSuccess("Institute details updated successfully");
      setEditMode(false);
    },
    onError: (error) => {
      console.error('Institute update error:', error);
      setToastMessage(error.response?.data?.message || "Failed to update institute details");
      setToastSeverity("error");
    }
  });

  const requestToEditProfileMutation = useMutation({
    mutationFn: profileService.requestToEditProfile,
    onSuccess: async (response) => {
      const message =
        response?.data?.message ||
        response?.message ||
        "Request to edit profile submitted successfully";
      setToastMessage(message);
      setToastSeverity("success");
      await queryClient.invalidateQueries({
        queryKey: ["teacherProfileDetails", user?.role?.toLowerCase()],
      });
    },
    onError: (error) => {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.message ||
        (status === 409
          ? "A profile update request is already pending approval."
          : "Failed to submit request");
      setToastMessage(message);
      setToastSeverity("error");
    },
  });

  // Update profile image mutation for all roles
  const updateProfileImageMutation = useMutation({
    mutationFn: (file) => {
      const formData = new FormData();
      formData.append('profileImage', file);
      
      // Use different endpoints based on user role
      if (isTeacherOrHOD) {
        return profileService.updateTeacherProfileImage(formData);
      } else {
        return profileService.updateProfileImage(formData);
      }
    },
    onSuccess: async (response) => {
      // Refresh the appropriate profile details API after successful photo upload
      if (isTeacherOrHOD) {
        await queryClient.invalidateQueries({ queryKey: ["teacherProfileDetails", user?.role?.toLowerCase()] });
        await queryClient.invalidateQueries({ queryKey: ["dashboardTeacherDetails", user?.role?.toLowerCase()] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ["instituteProfileDetails"] });
        await queryClient.invalidateQueries({ queryKey: ["adminInstituteProfileDetails"] });
        // Also refresh super admin details if user is super admin
        if (isSuperAdmin) {
          await queryClient.invalidateQueries({ queryKey: ["superAdminProfileDetails"] });
          await queryClient.invalidateQueries({ queryKey: ["superAdminInstituteProfileDetails"] });
        }
      }
      
      // Try to extract returned image path from response and update local user immediately
      const getUpdatedPayload = (res) => res?.data?.data ?? res?.data?.user ?? res?.data ?? res;
      const payload = getUpdatedPayload(response) || {};
      const returnedProfileImage = payload.profileImage ?? payload.profile_image ?? payload.avatar ?? null;
      let profileImageForStore = returnedProfileImage || user?.profileImage || user?.avatar || null;
      if (profileImageForStore) {
        // Append timestamp to bust browser cache
        profileImageForStore = `${profileImageForStore}${profileImageForStore.includes('?') ? '&' : '?'}t=${Date.now()}`;
        const updatedUser = { ...user, profileImage: profileImageForStore, avatar: profileImageForStore };
        dispatch(setUser(updatedUser));
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }

      setToastMessage("Profile image updated successfully");
      setToastSeverity("success");
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update profile image";
      setToastMessage(message);
      setToastSeverity("error");
    }
  });

  const removeProfileImageMutation = useMutation({
    mutationFn: () =>
      isTeacherOrHOD
        ? profileService.deleteTeacherProfileImage()
        : profileService.deleteWebProfileImage(),
    onSuccess: async () => {
      if (isTeacherOrHOD) {
        await queryClient.invalidateQueries({ queryKey: ["teacherProfileDetails", user?.role?.toLowerCase()] });
        await queryClient.invalidateQueries({ queryKey: ["dashboardTeacherDetails", user?.role?.toLowerCase()] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ["instituteProfileDetails"] });
        await queryClient.invalidateQueries({ queryKey: ["adminInstituteProfileDetails"] });
        if (isSuperAdmin) {
          await queryClient.invalidateQueries({ queryKey: ["superAdminProfileDetails"] });
          await queryClient.invalidateQueries({ queryKey: ["superAdminInstituteProfileDetails"] });
        }
      }
      const updatedUser = { ...user, profileImage: null, avatar: null };
      dispatch(setUser(updatedUser));
      try {
        localStorage.setItem("user", JSON.stringify(updatedUser));
      } catch (_) {}
      setAvatarImageError(false);
      setRemovePhotoConfirmOpen(false);
      setToastMessage("Profile photo removed");
      setToastSeverity("success");
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to remove profile photo";
      setToastMessage(message);
      setToastSeverity("error");
      setRemovePhotoConfirmOpen(false);
    },
  });

  const { hasVisiblePermission } = usePermissions();
  const hasProfileUpdatePerm = hasVisiblePermission("profile.update");
  const isTeacherOrHOD = ['teacher', 'hod'].includes(user?.role?.toLowerCase());
  const canManageOwnProfilePhoto =
    isAdmin || isInstitute || isSuperAdmin || isTeacherOrHOD;

  // Fetch teacher/HOD profile details when user clicks Profile in sidebar (role = teacher or hod)
  // Must be declared before isProfileApproved / canEditProfile which read from this response.
  const { data: teacherDetailsResponse, isLoading: isTeacherDetailsLoading } = useQuery({
    queryKey: ["teacherProfileDetails", user?.role?.toLowerCase()],
    queryFn: () => profileService.getTeacherUpdatedProfile(),
    enabled: isTeacherOrHOD,
    refetchOnMount: "always",
    staleTime: 0,
  });

  const isProfileApproved = Boolean(
    teacherDetailsResponse?.data?.data?.isApproved ??
      teacherDetailsResponse?.data?.isApproved ??
      user?.isApproved
  );
  // Super Admin / Institute Admin / platform Admin edit profile directly — no approval workflow.
  // Teacher/HOD still require profile.update + isApproved (or request-to-edit flow).
  const canEditProfile = isSuperAdmin || isInstitute || isAdmin
    ? true
    : hasProfileUpdatePerm && (!isTeacherOrHOD || isProfileApproved);

  // Fetch institute profile details when user clicks Profile in sidebar (role = institute) — always hit API on each visit
  const { data: instituteDetailsResponse, isLoading: isInstituteDetailsLoading } = useQuery({
    queryKey: ["instituteProfileDetails"],
    queryFn: () => profileService.getInstituteDetails(),
    enabled: isInstitute,
    refetchOnMount: "always",
    staleTime: 0,
    gcTime: 0,
  });

  // Fetch institute profile details when user clicks Profile in sidebar (role = admin) — always hit API on each visit
  const { data: adminInstituteDetailsResponse, isLoading: isAdminInstituteDetailsLoading } = useQuery({
    queryKey: ["adminInstituteProfileDetails"],
    queryFn: () => profileService.getInstituteDetails(),
    enabled: isAdmin,
    refetchOnMount: "always",
    staleTime: 0,
    gcTime: 0,
  });

  // Fetch admin profile details when user clicks Profile in sidebar (role = superadmin) — always hit API on each visit
  const { data: superAdminDetailsResponse, isLoading: isSuperAdminDetailsLoading } = useQuery({
    queryKey: ["superAdminProfileDetails"],
    queryFn: () => profileService.getSuperAdminDetails(),
    enabled: isSuperAdminPersonalMode,
    refetchOnMount: "always",
    staleTime: 0,
    gcTime: 0,
  });

  // Fetch institute details when superadmin is in institute context — so sidebar/header show institute photo immediately
  const { data: superAdminInstituteDetailsResponse, isLoading: isSuperAdminInstituteDetailsLoading, isError: isSuperAdminInstituteError } = useQuery({
    queryKey: ["superAdminInstituteProfileDetails", user?.operatingInstituteId, selectedInstitute?.id],
    queryFn: () => profileService.getInstituteDetails(),
    enabled: isSuperAdminInstituteMode,
    refetchOnMount: "always",
    staleTime: 0,
    gcTime: 0,
  });

  const extractProfileApiPayload = (response) => {
    if (!response) return null;
    const raw =
      response?.data?.data ??
      response?.data?.user ??
      response?.data ??
      response;
    return raw && typeof raw === "object" && Object.keys(raw).length > 0 ? raw : null;
  };

  const instituteProfileData = useMemo(() => {
    if (!showInstituteProfile) return null;
    if (isSuperAdminInstituteMode) {
      return extractProfileApiPayload(superAdminInstituteDetailsResponse);
    }
    if (isAdmin) {
      return extractProfileApiPayload(adminInstituteDetailsResponse);
    }
    if (isInstitute) {
      return extractProfileApiPayload(instituteDetailsResponse);
    }
    return null;
  }, [
    showInstituteProfile,
    isSuperAdminInstituteMode,
    isAdmin,
    isInstitute,
    superAdminInstituteDetailsResponse,
    adminInstituteDetailsResponse,
    instituteDetailsResponse,
  ]);

  const effectiveInstitute = useMemo(() => {
    if (!showInstituteProfile) return null;
    const base =
      normalizedRole === "superadmin"
        ? selectedInstitute
        : selectedInstitute ||
          user?.institute ||
          (user
            ? {
                name: user?.name || user?.instituteName,
                profileImage: user?.profileImage,
                avatar: user?.avatar,
              }
            : null);
    if (!instituteProfileData) return base;
    return {
      ...(base || {}),
      name:
        instituteProfileData.name ??
        instituteProfileData.instituteName ??
        base?.name,
      profileImage:
        instituteProfileData.profileImage ??
        instituteProfileData.profile_image ??
        base?.profileImage,
      avatar:
        instituteProfileData.profileImage ??
        instituteProfileData.profile_image ??
        base?.avatar,
      email: instituteProfileData.email ?? base?.email,
      phoneNumber:
        instituteProfileData.phoneNumber ??
        instituteProfileData.phone ??
        base?.phoneNumber,
    };
  }, [
    showInstituteProfile,
    normalizedRole,
    selectedInstitute,
    user,
    instituteProfileData,
  ]);

  const profileImageUrl = useMemo(() => {
    const src = showInstituteProfile
      ? effectiveInstitute?.profileImage || effectiveInstitute?.avatar
      : user?.profileImage || user?.avatar;
    return src && String(src).trim() ? src : null;
  }, [showInstituteProfile, effectiveInstitute, user]);

  useEffect(() => {
    setAvatarImageError(false);
  }, [profileImageUrl]);

  const profileContact = useMemo(() => {
    const raw =
      profileContactResponse?.data?.data ?? profileContactResponse?.data ?? {};
    const institutePhone =
      instituteProfileData?.phoneNumber ?? instituteProfileData?.phone;
    const instituteEmail = instituteProfileData?.email;
    return {
      phone: showInstituteProfile
        ? institutePhone ?? raw.phone ?? raw.phoneNumber ?? null
        : raw.phone ?? raw.phoneNumber ?? user?.phoneNumber ?? user?.phone ?? null,
      email: showInstituteProfile
        ? instituteEmail ?? raw.email ?? null
        : raw.email ?? user?.email ?? null,
      address: showInstituteProfile
        ? [
            instituteProfileData?.street,
            instituteProfileData?.city,
            instituteProfileData?.state,
            instituteProfileData?.country,
          ]
            .filter(Boolean)
            .join(", ") ||
          raw.address ||
          user?.address ||
          null
        : raw.address ?? user?.address ?? null,
      emergency:
        raw.emergency ?? raw.emergencyPhone ?? raw.emergency_contact ?? null,
    };
  }, [
    profileContactResponse,
    showInstituteProfile,
    instituteProfileData,
    user,
  ]);

  const profileFormSource =
    showInstituteProfile && instituteProfileData ? instituteProfileData : user;

  // Sync fetched institute details into Redux and localStorage so profile shows updated data without logout
  useEffect(() => {
    if (!isInstitute || !instituteDetailsResponse) return;
    const raw =
      instituteDetailsResponse?.data?.data ??
      instituteDetailsResponse?.data?.user ??
      instituteDetailsResponse?.data ??
      instituteDetailsResponse;
    if (!raw || typeof raw !== "object" || Object.keys(raw).length === 0)
      return;
    const payload = {
      ...raw,
      name: raw.name ?? raw.instituteName,
      phoneNumber: raw.phoneNumber ?? raw.phone_number ?? raw.phone,
      pinCode: raw.pinCode ?? raw.pincode ?? raw.pin_code,
      street: raw.street,
      city: raw.city,
      state: raw.state,
      country: raw.country,
      website: raw.website,
    };
    const updatedUser = { ...user, ...payload };
    dispatch(setUser(updatedUser));
    localStorage.setItem("user", JSON.stringify(updatedUser));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when API response arrives, not when user changes after dispatch
  }, [isInstitute, instituteDetailsResponse, dispatch]);

  // Sync fetched admin institute details into Redux and localStorage so profile shows updated data without logout
  useEffect(() => {
    if (!isAdmin || !adminInstituteDetailsResponse) return;
    const raw =
      adminInstituteDetailsResponse?.data?.data ??
      adminInstituteDetailsResponse?.data?.user ??
      adminInstituteDetailsResponse?.data ??
      adminInstituteDetailsResponse;
    if (!raw || typeof raw !== "object" || Object.keys(raw).length === 0)
      return;
    const payload = {
      ...raw,
      name: raw.name ?? raw.instituteName,
      phoneNumber: raw.phoneNumber ?? raw.phone_number ?? raw.phone,
      pinCode: raw.pinCode ?? raw.pincode ?? raw.pin_code,
      street: raw.street,
      city: raw.city,
      state: raw.state,
      country: raw.country,
      website: raw.website,
      profileImage: raw.profileImage,
    };
    const updatedUser = { ...user, ...payload };
    dispatch(setUser(updatedUser));
    localStorage.setItem("user", JSON.stringify(updatedUser));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when API response arrives, not when user changes after dispatch
  }, [isAdmin, adminInstituteDetailsResponse, dispatch]);

  // Sync fetched superadmin personal details (only when not managing an institute)
  useEffect(() => {
    if (!isSuperAdminPersonalMode || !superAdminDetailsResponse) return;
    const raw =
      superAdminDetailsResponse?.data?.data ??
      superAdminDetailsResponse?.data?.user ??
      superAdminDetailsResponse?.data ??
      superAdminDetailsResponse;
    if (!raw || typeof raw !== "object" || Object.keys(raw).length === 0)
      return;
    const payload = {
      firstName: raw.firstName ?? raw.first_name,
      lastName: raw.lastName ?? raw.last_name ?? "",
      phoneNumber: raw.phoneNumber ?? raw.phone_number ?? raw.phone,
      pinCode: raw.pinCode ?? raw.pincode ?? raw.pin_code,
      gender: raw.gender,
      dob: raw.dob ?? raw.date_of_birth,
      profileImage: raw.profileImage ?? raw.profile_image,
      name: raw.name ?? raw.fullName,
    };
    const updatedUser = {
      ...user,
      ...payload,
      instituteLogo: user?.instituteLogo,
      instituteName: user?.instituteName,
      website: user?.website,
      street: user?.street,
      city: user?.city,
      state: user?.state,
      country: user?.country,
    };
    dispatch(setUser(updatedUser));
    localStorage.setItem("user", JSON.stringify(updatedUser));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when API response arrives, not when user changes after dispatch
  }, [isSuperAdminPersonalMode, superAdminDetailsResponse, dispatch]);

  // Sync institute details when superadmin is managing an institute
  useEffect(() => {
    if (!instituteProfileData || !isSuperAdminInstituteMode) return;
    const raw = instituteProfileData;
    const payload = {
      ...raw,
      name: raw.name ?? raw.instituteName,
      instituteName: raw.name ?? raw.instituteName,
      phoneNumber: raw.phoneNumber ?? raw.phone_number ?? raw.phone,
      pinCode: raw.pinCode ?? raw.pincode ?? raw.pin_code,
      street: raw.street,
      city: raw.city,
      state: raw.state,
      country: raw.country,
      website: raw.website,
      profileImage: raw.profileImage ?? raw.profile_image,
      email: raw.email,
      latitude: raw.latitude,
      longitude: raw.longitude,
      instituteDistanceRadius: raw.instituteDistanceRadius,
      teacherClassDistanceRadius: raw.teacherClassDistanceRadius,
      attendanceTokenRefreshSeconds: raw.attendanceTokenRefreshSeconds ?? null,
    };
    const updatedUser = { ...user, ...payload };
    dispatch(setUser(updatedUser));
    localStorage.setItem("user", JSON.stringify(updatedUser));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when API response arrives
  }, [instituteProfileData, isSuperAdminInstituteMode, dispatch]);

  const profileEditRequestStatusFromApi =
    teacherDetailsResponse?.data?.data?.profileEditRequestStatus ??
    teacherDetailsResponse?.data?.profileEditRequestStatus;

  const profileEditRequestStatus = String(
    teacherDetailsResponse != null
      ? profileEditRequestStatusFromApi ?? ""
      : user?.profileEditRequestStatus ?? ""
  ).toLowerCase();
  const hasPendingProfileEditRequest = profileEditRequestStatus === "pending";
  const pendingProfileEditMessage =
    user?.role?.toLowerCase() === "hod"
      ? "Your profile edit permission request is pending institute approval."
      : "A profile update request is already pending approval.";
  const profileEditRequestsPath = `/${String(user?.role || "hod").toLowerCase()}/requests${
    profileEditRequestStatus === "pending" ? "?status=pending" : ""
  }`;
  const canRequestProfileEdit =
    hasProfileUpdatePerm &&
    isTeacherOrHOD &&
    !isProfileApproved &&
    !hasPendingProfileEditRequest;

  const isProfileDataLoading =
    (isInstitute && isInstituteDetailsLoading) ||
    (isAdmin && isAdminInstituteDetailsLoading) ||
    (isSuperAdminPersonalMode && isSuperAdminDetailsLoading) ||
    (isSuperAdminInstituteMode && isSuperAdminInstituteDetailsLoading) ||
    (isTeacherOrHOD && isTeacherDetailsLoading);

  // Sync fetched teacher details into Redux and localStorage so profile shows updated data without logout
  useEffect(() => {
    if (!isTeacherOrHOD || !teacherDetailsResponse) return;
    const raw =
      teacherDetailsResponse?.data?.data ??
      teacherDetailsResponse?.data?.user ??
      teacherDetailsResponse?.data ??
      teacherDetailsResponse;
    if (!raw || typeof raw !== "object" || Object.keys(raw).length === 0)
      return;
    // Normalize snake_case to camelCase if API returns snake_case
    const payload = {
      ...raw,
      id: raw.id ?? "",
      userId: raw.userId ?? raw.user_id ?? "",
      teacherLoginID: raw.teacherLoginID ?? raw.teacher_login_id ?? "",
      firstName: raw.firstName ?? raw.first_name,
      lastName: raw.lastName ?? raw.last_name ?? "",
      fullName:
        `${raw.firstName ?? raw.first_name ?? ""} ${raw.lastName ?? raw.last_name ?? ""}`.trim() ||
        raw.fullName ||
        raw.full_name ||
        "",
      phoneNumber: raw.phoneNumber ?? raw.phone_number ?? raw.phone,
      // Some APIs return street/address as a single field; normalize both to `address`
      address: raw.address ?? raw.street ?? "",
      dateOfBirth: raw.dateOfBirth ?? raw.date_of_birth ?? raw.dob,
      dob: raw.dob ?? raw.date_of_birth ?? raw.dateOfBirth,
      role: raw.role ?? "",
      fcmToken: raw.fcmToken ?? "",
      city: raw.city ?? "",
      state: raw.state ?? "",
      country: raw.country ?? "",
      pinCode: raw.pinCode ?? raw.pincode ?? raw.pin_code ?? "",
      street: raw.street ?? raw.address ?? "",
      isApproved: raw.isApproved,
      profileEditRequestStatus:
        raw.profileEditRequestStatus === "pending" ? "pending" : null,
      profileEditRequestId:
        raw.profileEditRequestStatus === "pending"
          ? raw.profileEditRequestId ?? null
          : null,
      qualification: raw.qualification ?? "",
      experienceYears: raw.experienceYears ?? "",
      joiningDate: raw.joiningDate ?? "",
      designation: raw.designation ?? "",
      departmentName: raw.departmentName ?? "",
      salary: raw.salary ?? "",
      profileImage: raw.profileImage ?? "",
      instituteName: raw.instituteName ?? "",
      instituteLogo: raw.instituteLogo ?? "",
    };
    const updatedUser = { ...user, ...payload };
    dispatch(setUser(updatedUser));
    localStorage.setItem("user", JSON.stringify(updatedUser));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when API response arrives, not when user changes after dispatch
  }, [isTeacherOrHOD, teacherDetailsResponse, dispatch]);

  // Debug function to log form state
  const logFormState = (values, errors) => {
    console.log('Is form valid?', Object.keys(errors).length === 0);
  };

  // Block number keys in city/state/country (letters and spaces only)
  const blockNumberKeyDown = (e) => {
    if (/^[0-9]$/.test(e.key)) e.preventDefault();
  };
  const handleCityStateCountryChange = (field) => (e) => {
    const value = e.target.value.replace(/\d/g, '');
    formik.setFieldValue(field, value);
  };

  const formik = useFormik({
    initialValues: {
      id: profileFormSource?.id || user?.id || "",
      userId: profileFormSource?.userId || user?.userId || "",
      teacherLoginID: user?.teacherLoginID || "",
      name:
        profileFormSource?.name ||
        profileFormSource?.instituteName ||
        user?.fullName ||
        "",
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      fullName: `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.fullName || "",
      email: profileFormSource?.email || user?.email || "",
      phone:
        profileFormSource?.phoneNumber ||
        profileFormSource?.phone ||
        user?.phoneNumber ||
        user?.phone ||
        "",
      phoneNumber:
        profileFormSource?.phoneNumber ||
        profileFormSource?.phone ||
        user?.phoneNumber ||
        user?.phone ||
        "",
      address: profileFormSource?.address || user?.address || "",
      city: profileFormSource?.city || user?.city || "",
      state: profileFormSource?.state || user?.state || "",
      pinCode:
        profileFormSource?.pinCode ||
        profileFormSource?.pincode ||
        user?.pinCode ||
        user?.pincode ||
        "",
      website: profileFormSource?.website || user?.website || "",
      country: profileFormSource?.country || user?.country || "",
      street: profileFormSource?.street || user?.street || "",
      latitude: profileFormSource?.latitude || user?.latitude || "",
      longitude: profileFormSource?.longitude || user?.longitude || "",
      instituteDistanceRadius:
        profileFormSource?.instituteDistanceRadius ||
        user?.instituteDistanceRadius ||
        500,
      teacherClassDistanceRadius:
        profileFormSource?.teacherClassDistanceRadius ||
        user?.teacherClassDistanceRadius ||
        200,
      // Raw stored value may be null -> keep the field blank (helper text explains the 60s default)
      attendanceTokenRefreshSeconds:
        profileFormSource?.attendanceTokenRefreshSeconds ??
        user?.attendanceTokenRefreshSeconds ??
        "",
      gender: user?.gender || "",
      dob: user?.dob || "",
      role: user?.role?.name || user?.role || "",
      fcmToken: user?.fcmToken || "",
      isApproved: String(user?.isApproved ?? ""),
      qualification: user?.qualification || "",
      experienceYears: user?.experienceYears ?? "",
      joiningDate: user?.joiningDate || "",
      designation: user?.designation || "",
      departmentName: user?.departmentName || "",
      salary: user?.salary ?? "",
      profileImage:
        profileFormSource?.profileImage ||
        profileFormSource?.profile_image ||
        user?.profileImage ||
        "",
      instituteName:
        profileFormSource?.name ||
        profileFormSource?.instituteName ||
        user?.instituteName ||
        "",
      instituteLogo: user?.instituteLogo || "",
    },
    validationSchema: validationSchema,
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: !isSuperAdminInstituteMode,
    validationContext: {
      isInstitute,
      isInstituteOrAdmin: isInstitute || isAdmin || isSuperAdminInstituteMode,
      isSuperAdmin: isSuperAdminPersonalMode,
      isTeacherOrHOD: ['teacher', 'hod'].includes(user?.role?.toLowerCase()),
    },
    isInitialValid: false,
    validate: (values) => {
      const errors = {};
      // Explicit pinCode validation for institute/admin so live validation works (avoids reliance on schema .when context)
      if (isInstitute || isAdmin || isSuperAdminInstituteMode) {
        const pin = values.pinCode != null ? String(values.pinCode).trim() : '';
        if (!pin) errors.pinCode = 'Pincode is required';
        else if (!/^[0-9]{3,9}$/.test(pin)) errors.pinCode = 'Pincode must be 3-9 digits';
      }
      logFormState(values, errors);
      return errors;
    },
    onSubmit: async (values, { setSubmitting, setFieldError, setTouched }) => {
      try {
        // For institute or admin: do not hit API if required fields are blank — show validation instead
        if (isInstitute || isAdmin || isSuperAdminInstituteMode) {
          const required = ['name', 'street', 'city', 'state', 'country', 'pinCode', 'website'];
          const blank = required.filter((f) => !(values[f] && String(values[f]).trim()));
          if (blank.length > 0) {
            const messages = {
              name: 'Institute name is required',
              street: 'Street is required',
              city: 'City is required',
              state: 'State is required',
              country: 'Country is required',
              pinCode: 'Pincode is required',
              website: 'Website is required',
            };
            blank.forEach((field) => setFieldError(field, messages[field] || `${field} is required`));
            setTouched((prev) => ({ ...prev, ...Object.fromEntries(blank.map((f) => [f, true])) }));
            setSubmitting(false);
            setToastMessage('Please fill all required fields.');
            setToastSeverity('error');
            return;
          }
        }

        // For superadmin personal profile (not managing an institute)
        if (isSuperAdminPersonalMode) {
          const required = ['firstName', 'lastName', 'email', 'phone', 'gender', 'dob'];
          const blank = required.filter((f) => {
            const v = values[f];
            if (f === 'dob') return !(v && String(v).trim());
            return !(v != null && String(v).trim());
          });
          if (blank.length > 0) {
            const messages = {
              firstName: 'First name is required',
              lastName: 'Last name is required',
              email: 'Email is required',
              phone: 'Phone is required',
              gender: 'Gender is required',
              dob: 'Date of birth is required',
            };
            blank.forEach((field) => setFieldError(field, messages[field] || `${field} is required`));
            if (blank.includes('phone')) setFieldError('phoneNumber', 'Phone is required');
            const touchedFromBlank = Object.fromEntries(blank.map((f) => [f, true]));
            if (blank.includes('phone')) touchedFromBlank.phoneNumber = true;
            setTouched((prev) => ({ ...prev, ...touchedFromBlank }));
            setSubmitting(false);
            setToastMessage('Please fill all required fields.');
            setToastSeverity('error');
            return;
          }
        }

        // For teacher or HOD: do not hit API if required fields are blank — show validation instead
        if (isTeacherOrHOD) {
          const required = [
            'firstName',
            'lastName',
            'email',
            'phone',
            'gender',
            'dob',
            'qualification',
            'experienceYears',
            'joiningDate',
            'designation',
            'departmentName',
            'salary',
            'street',
            'city',
            'state',
            'country',
            'pinCode',
          ];
          const blank = required.filter((f) => {
            const v = values[f];
            if (f === 'dob') return !(v && String(v).trim());
            return !(v != null && String(v).trim());
          });
          if (blank.length > 0) {
            const messages = {
              firstName: 'First name is required',
              lastName: 'Last name is required',
              email: 'Email is required',
              phone: 'Phone is required',
              gender: 'Gender is required',
              dob: 'Date of birth is required',
              qualification: 'Qualification is required',
              experienceYears: 'Experience is required',
              joiningDate: 'Joining date is required',
              designation: 'Designation is required',
              departmentName: 'Department name is required',
              salary: 'Salary is required',
              street: 'Street is required',
              city: 'City is required',
              state: 'State is required',
              country: 'Country is required',
              pinCode: 'Pincode is required',
            };
            blank.forEach((field) => setFieldError(field, messages[field] || `${field} is required`));
            if (blank.includes('phone')) setFieldError('phoneNumber', 'Phone is required');
            const touchedFromBlank = Object.fromEntries(blank.map((f) => [f, true]));
            if (blank.includes('phone')) touchedFromBlank.phoneNumber = true;
            setTouched((prev) => ({ ...prev, ...touchedFromBlank }));
            setSubmitting(false);
            setToastMessage('Please fill all required fields.');
            setToastSeverity('error');
            return;
          }
        }

        // Get updated payload from API response (supports { data: { data: {...} } }, { data: { user: {...} } }, or { data: {...} })
        const getUpdatedPayload = (res) =>
          res?.data?.data ?? res?.data?.user ?? res?.data ?? {};

        if (isInstitute || isSuperAdminInstituteMode) {
          const response = await updateInstituteDetails.mutateAsync(values);
          const payload = getUpdatedPayload(response);
          if (Object.keys(payload).length > 0 || response?.data) {
            const updatedUser = { ...user, ...payload };
            localStorage.setItem("user", JSON.stringify(updatedUser));
            dispatch(setUser(updatedUser));
            await queryClient.invalidateQueries({ queryKey: ["instituteProfileDetails"] });
            await queryClient.invalidateQueries({ queryKey: ["superAdminInstituteProfileDetails"] });
            await queryClient.refetchQueries({ queryKey: ["instituteProfileDetails"] });
            await queryClient.refetchQueries({ queryKey: ["superAdminInstituteProfileDetails"] });
            setSuccess("Profile updated successfully");
            setEditMode(false);
          }
        } else if (isSuperAdminPersonalMode) {
          const { firstName, lastName, phoneNumber, gender, dob } = values;
          const response = await updateProfileMutation.mutateAsync({
            firstName,
            lastName,
            phoneNumber,
            gender,
            dob,
            role: 'superadmin',
          });
          const payload = getUpdatedPayload(response);
          if (Object.keys(payload).length > 0 || response?.data) {
            // Preserve profile photo: API may omit it or return snake_case (profile_image)
            const profileImage =
              payload.profileImage ?? payload.profile_image ?? payload.avatar ?? user?.profileImage ?? user?.avatar;
            // Explicitly set firstName/lastName so empty string is preserved when API omits them
            const firstName = payload.firstName ?? payload.first_name ?? values.firstName ?? user?.firstName ?? "";
            const lastName = payload.lastName ?? payload.last_name ?? values.lastName ?? user?.lastName ?? "";
            const updatedUser = {
              ...user,
              ...payload,
              firstName,
              lastName,
              role: (payload.role || user?.role || "superadmin").toString().toLowerCase(),
              profileImage,
              avatar: profileImage,
            };
            localStorage.setItem("user", JSON.stringify(updatedUser));
            dispatch(setUser(updatedUser));
            await queryClient.invalidateQueries({ queryKey: ["superAdminProfileDetails"] });
            await queryClient.refetchQueries({ queryKey: ["superAdminProfileDetails"] });
            setSuccess("Profile updated successfully");
            setEditMode(false);
          }
        } else if (isTeacherOrHOD) {
          const response = await profileService.updateTeacherProfile({
            id: values.id,
            userId: values.userId,
            teacherLoginID: values.teacherLoginID,
            firstName: values.firstName,
            lastName: values.lastName,
            fullName: `${values.firstName || ""} ${values.lastName || ""}`.trim(),
            email: values.email,
            phoneNumber: values.phoneNumber || values.phone,
            gender: values.gender,
            dob: values.dob,
            role: values.role,
            fcmToken: values.fcmToken,
            isApproved: values.isApproved,
            qualification: values.qualification,
            experienceYears: values.experienceYears,
            joiningDate: values.joiningDate,
            designation: values.designation,
            departmentName: values.departmentName,
            salary: values.salary,
            street: values.street,
            city: values.city,
            state: values.state,
            country: values.country,
            pinCode: values.pinCode,
            profileImage: values.profileImage,
            instituteName: values.instituteName,
            instituteLogo: values.instituteLogo
          });
          const payload = getUpdatedPayload(response);
          if (Object.keys(payload).length > 0 || response?.data) {
            const firstName = payload.firstName ?? payload.first_name ?? values.firstName ?? user?.firstName ?? "";
            const lastName = payload.lastName ?? payload.last_name ?? values.lastName ?? user?.lastName ?? "";
            const fullName = `${firstName || ""} ${lastName || ""}`.trim();
            const updatedUser = { ...user, ...payload, firstName, lastName, fullName };
            localStorage.setItem("user", JSON.stringify(updatedUser));
            dispatch(setUser(updatedUser));
            await queryClient.invalidateQueries({ queryKey: ['user'] });
            await queryClient.invalidateQueries({ queryKey: ['teacherProfileDetails', user?.role?.toLowerCase()] });
            await queryClient.refetchQueries({ queryKey: ['teacherProfileDetails', user?.role?.toLowerCase()] });
            setSuccess("Profile updated successfully");
            setEditMode(false);
          }
        } else {
          const response = await updateProfileMutation.mutateAsync(values);
          const payload = getUpdatedPayload(response);
          if (Object.keys(payload).length > 0 || response?.data) {
            const updatedUser = { ...user, ...payload };
            localStorage.setItem("user", JSON.stringify(updatedUser));
            dispatch(setUser(updatedUser));
            await queryClient.invalidateQueries({ queryKey: ['user'] });
            await queryClient.invalidateQueries({ queryKey: ["adminInstituteProfileDetails"] });
            await queryClient.refetchQueries({ queryKey: ["adminInstituteProfileDetails"] });
            setSuccess("Profile updated successfully");
            setEditMode(false);
          }
        }
      } catch (error) {
        console.error('Form submission error:', error);
        setToastMessage(error.response?.data?.message || "Failed to update profile");
        setToastSeverity("error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!instituteProfileData || !showInstituteProfile) return;
    const raw = instituteProfileData;
    formik.setValues((prev) => ({
      ...prev,
      name: raw.name ?? raw.instituteName ?? prev.name,
      email: raw.email ?? prev.email,
      phone: raw.phoneNumber ?? raw.phone ?? prev.phone,
      phoneNumber: raw.phoneNumber ?? raw.phone ?? prev.phoneNumber,
      street: raw.street ?? prev.street,
      city: raw.city ?? prev.city,
      state: raw.state ?? prev.state,
      country: raw.country ?? prev.country,
      pinCode: raw.pinCode ?? raw.pincode ?? prev.pinCode,
      website: raw.website ?? prev.website,
      latitude: raw.latitude ?? prev.latitude,
      longitude: raw.longitude ?? prev.longitude,
      instituteDistanceRadius: raw.instituteDistanceRadius ?? prev.instituteDistanceRadius,
      teacherClassDistanceRadius: raw.teacherClassDistanceRadius ?? prev.teacherClassDistanceRadius,
      // Raw value may be null -> blank field; only overwrite when the API actually returned a number
      attendanceTokenRefreshSeconds:
        raw.attendanceTokenRefreshSeconds ?? prev.attendanceTokenRefreshSeconds ?? "",
      profileImage: raw.profileImage ?? raw.profile_image ?? prev.profileImage,
    }));
  }, [instituteProfileData, showInstituteProfile]);

  const passwordFormik = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    validationSchema: passwordSchema,
    onSubmit: async (values) => {
      try {
        const res = await changePasswordMutation.mutateAsync({
          oldPassword: values.currentPassword,
          newPassword: values.newPassword,
          confirmNewPassword: values.confirmNewPassword,
        });

        const msg =
          res?.data?.message ||
          res?.message ||
          "Password changed successfully";

        setSuccess("Password changed successfully");
        setToastMessage(msg);
        setToastSeverity("success");

        passwordFormik.resetForm({
          values: {
            currentPassword: "",
            newPassword: "",
            confirmNewPassword: "",
          },
        });
      } catch (error) {
        // Prevent navigation on error - stay on profile page
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to change password";

        setSuccess("Failed to change password");
        setToastMessage(msg);
        setToastSeverity("error");
        
        // Don't let the error propagate to avoid any global error handlers
        return;
      }
    },
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSuccess(null);
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleCloseToast = () => {
    setToastMessage(null);
  };

  // Handle profile image upload
  const handleProfileImageClick = () => {
    if (canManageOwnProfilePhoto) {
      fileInputRef.current?.click();
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    // Validation for profile photo — same rules for every role (admin, institute, superadmin, teacher, hod)
    const fileValidation = validateProfilePhoto(file);
    if (!fileValidation.valid) {
      setToastMessage(fileValidation.error);
      setToastSeverity("error");
      return;
    }

    const dimensionValidation = await validateProfilePhotoDimensions(file);
    if (!dimensionValidation.valid) {
      setToastMessage(dimensionValidation.error);
      setToastSeverity("error");
      return;
    }

    updateProfileImageMutation.mutate(file);
  };

  const profileDisplayName = showInstituteProfile
    ? instituteProfileData?.name ??
      user?.name ??
      user?.instituteName ??
      effectiveInstitute?.name ??
      "Institute"
    : `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.fullName || "User";
  const profileDisplayRole =
    showInstituteProfile && isSuperAdmin ? "Institute" : user?.role?.name || user?.role;
  const profileDisplayEmail =
    (showInstituteProfile ? instituteProfileData?.email : null) ?? user?.email ?? "Not provided";

  const userInfo = [
    { icon: <Email />, label: "Email", value: profileDisplayEmail },
    {
      icon: <Phone />,
      label: "Phone",
      value:
        (showInstituteProfile
          ? instituteProfileData?.phoneNumber ?? instituteProfileData?.phone
          : null) ??
        user?.phoneNumber ??
        user?.phone ??
        profileContact.phone ??
        "Not provided",
    },
    {
      icon: <LocationOn />,
      label: "Address",
      value: showInstituteProfile
        ? [instituteProfileData?.street, instituteProfileData?.city, instituteProfileData?.state, instituteProfileData?.country]
            .filter(Boolean)
            .join(", ") ||
          profileContact.address ||
          "Not provided"
        : profileContact.address || "Not provided",
    },
    { icon: <School />, label: "Role", value: profileDisplayRole },
    // {
    //   icon: <Business />,
    //   label: "Department",
    //   value: user?.department?.name || "N/A",
    // },
    // {
    //   icon: <SupervisorAccount />,
    //   label: "Reporting To",
    //   value: user?.reportingTo?.name || user?.reportingManager || "N/A",
    // },
  ];

  const calculateAge = (dobValue) => {
    if (!dobValue) return null;
    const dob = new Date(dobValue);
    if (Number.isNaN(dob.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const monthDiff = now.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) age -= 1;
    return age >= 0 ? age : null;
  };

  const computedAge = calculateAge(user?.dob || user?.dateOfBirth);
  if (computedAge !== null && !isSuperAdminInstituteMode) {
    userInfo.push({ icon: <School />, label: "Age", value: `${computedAge} years` });
  }
  if (profileContact.emergency) {
    userInfo.push({ icon: <SupervisorAccount />, label: "Emergency", value: profileContact.emergency });
  }
  if (isTeacherOrHOD) {
    userInfo.push(
      { icon: <Business />, label: "Teacher Login ID", value: user?.teacherLoginID || "Not provided" },
      { icon: <Business />, label: "Designation", value: user?.designation || "Not provided" },
      { icon: <Business />, label: "Department", value: user?.departmentName || "Not provided" },
      { icon: <School />, label: "Qualification", value: user?.qualification || "Not provided" },
      { icon: <School />, label: "Experience", value: user?.experienceYears !== undefined && user?.experienceYears !== null && user?.experienceYears !== "" ? `${user?.experienceYears} years` : "Not provided" },
      { icon: <Business />, label: "Institute", value: user?.instituteName || "Not provided" }
    );
  }

  if (isProfileDataLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 320,
          width: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {isSuperAdminInstituteMode && isSuperAdminInstituteError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load institute profile details. Please exit and re-enter Manage Institute, then try again.
        </Alert>
      )}
      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <Box
                sx={{
                  position: "relative",
                  width: 120,
                  height: 120,
                  mx: "auto",
                  mb: 2,
                }}
              >
                <Avatar
                  src={profileImageUrl && !avatarImageError ? profileImageUrl : undefined}
                  imgProps={{ onError: () => setAvatarImageError(true) }}
                  sx={{
                    width: 120,
                    height: 120,
                    fontSize: 48,
                    bgcolor: "primary.main",
                    cursor: canManageOwnProfilePhoto ? "pointer" : "default",
                  }}
                  onClick={handleProfileImageClick}
                >
                  {showInstituteProfile && effectiveInstitute && !profileImageUrl
                    ? getAvatarInitial(effectiveInstitute)
                    : getAvatarInitial(user)}
                </Avatar>
                {canManageOwnProfilePhoto && profileImageUrl && (
                  <Tooltip title="Remove photo">
                    <Box component="span" sx={{ position: "absolute", bottom: 4, left: 4, lineHeight: 0 }}>
                      <IconButton
                        aria-label="Remove profile photo"
                        sx={{
                          bgcolor: "error.main",
                          color: "error.contrastText",
                          border: "2px solid",
                          borderColor: "background.paper",
                          boxShadow: 1,
                          "&:hover": { bgcolor: "error.dark" },
                          "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
                        }}
                        size="small"
                        onClick={() => setRemovePhotoConfirmOpen(true)}
                        disabled={removeProfileImageMutation.isPending || updateProfileImageMutation.isPending}
                      >
                        {removeProfileImageMutation.isPending ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <DeleteOutline fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </Tooltip>
                )}
                {canManageOwnProfilePhoto && (
                  <Tooltip title="Change photo">
                    <Box component="span" sx={{ position: "absolute", bottom: 4, right: 4, lineHeight: 0 }}>
                      <IconButton
                        aria-label="Change profile photo"
                        sx={{
                          bgcolor: "primary.main",
                          color: "white",
                          border: "2px solid",
                          borderColor: "background.paper",
                          boxShadow: 1,
                          "&:hover": { bgcolor: "primary.dark" },
                          "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
                        }}
                        size="small"
                        onClick={handleProfileImageClick}
                        disabled={updateProfileImageMutation.isPending || removeProfileImageMutation.isPending}
                      >
                        {updateProfileImageMutation.isPending ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <PhotoCamera fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </Tooltip>
                )}
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </Box>
              <Typography variant="h5" fontWeight={600}>
                {profileDisplayName}
              </Typography>
              <Chip
                label={profileDisplayRole}
                color="primary"
                size="small"
                sx={{ mt: 1, textTransform: "capitalize" }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {profileDisplayEmail}
              </Typography>

              <Divider sx={{ my: 3 }} />

              <List disablePadding>
                {userInfo.map((info, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {info.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={info.label}
                      secondary={info.value}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                      }}
                      secondaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Profile Details */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
                <Tab
                  icon={<Person />}
                  label="Personal Info"
                  iconPosition="start"
                />
                <Tab icon={<Lock />} label="Security" iconPosition="start" />
                <Tab icon={<Notifications />} label="Notifications" iconPosition="start" />
              </Tabs>

              {success && (
                <Alert
                  severity="success"
                  sx={{ mb: 2 }}
                  onClose={() => setSuccess(null)}
                >
                  {success}
                </Alert>
              )}

              {activeTab === 0 && (
                <Box component="form" onSubmit={formik.handleSubmit} noValidate>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}
                  >
                    {isTeacherOrHOD && !editMode && (
                      <Button
                        variant="outlined"
                        onClick={() => navigate(profileEditRequestsPath)}
                        sx={{ mr: 1 }}
                      >
                        View Profile Edit Requests
                      </Button>
                    )}
                    {canRequestProfileEdit && (
                      <Button
                        variant="contained"
                        sx={{ mr: 1 }}
                        onClick={() => requestToEditProfileMutation.mutate()}
                        disabled={requestToEditProfileMutation.isPending}
                      >
                        {requestToEditProfileMutation.isPending
                          ? "Requesting..."
                          : "Request To Edit"}
                      </Button>
                    )}
                    {hasPendingProfileEditRequest && !editMode && (
                      <Alert severity="warning" sx={{ mr: 1, flex: 1 }}>
                        {pendingProfileEditMessage}
                      </Alert>
                    )}
                    {canRequestProfileEdit && !editMode && (
                      <Alert severity="info" sx={{ mr: 1 }}>
                        Profile editing will be available once your request is approved by the institute.
                      </Alert>
                    )}
                    {editMode ? (
                      <>
                        <Button
                          type="button"
                          startIcon={<Cancel />}
                          onClick={() => {
                            formik.resetForm();
                            setEditMode(false);
                          }}
                          sx={{ mr: 1 }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={formik.isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Save />}
                          disabled={formik.isSubmitting}
                          onClick={() => {
                            // Manually trigger validation — for institute/admin don't hit API if required fields are blank
                            formik.validateForm().then(errors => {
                              if (Object.keys(errors).length === 0) {
                                formik.submitForm();
                              } else {
                                // Show validation on all required fields by marking them touched
                                const touchedFromErrors = Object.fromEntries(
                                  Object.keys(errors).map((k) => [k, true])
                                );
                                formik.setTouched((prev) => ({ ...prev, ...touchedFromErrors }));
                                setToastMessage('Please fill all required fields.');
                                setToastSeverity('error');
                              }
                            });
                          }}
                        >
                          {formik.isSubmitting ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </>
                    ) : (
                      (!isTeacherOrHOD || canEditProfile) && (
                        <Button
                          variant="outlined"
                          startIcon={<Edit />}
                          onClick={() => {
                            if (canEditProfile) {
                              setEditMode(true);
                            }
                          }}
                          disabled={!canEditProfile}
                        >
                          Edit Profile
                        </Button>
                      )
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    {showInstituteProfile ? (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          {...requiredTextFieldProps("Name")}
                          name="name"
                          value={formik.values.name}
                          onChange={handleCityStateCountryChange('name')}
                          onKeyDown={blockNumberKeyDown}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.name &&
                            Boolean(formik.errors.name)
                          }
                          helperText={
                            formik.touched.name && formik.errors.name
                          }
                          disabled={!editMode}
                        />
                      </Grid>
                    ) : (
                      <>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            {...requiredTextFieldProps("First Name")}
                            name="firstName"
                            value={formik.values.firstName}
                            onChange={handleCityStateCountryChange('firstName')}
                            onKeyDown={blockNumberKeyDown}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.firstName &&
                              Boolean(formik.errors.firstName)
                            }
                            helperText={
                              formik.touched.firstName && formik.errors.firstName
                            }
                            disabled={!editMode}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            {...requiredTextFieldProps("Last Name")}
                            name="lastName"
                            value={formik.values.lastName}
                            onChange={handleCityStateCountryChange('lastName')}
                            onKeyDown={blockNumberKeyDown}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.lastName &&
                              Boolean(formik.errors.lastName)
                            }
                            helperText={
                              formik.touched.lastName && formik.errors.lastName
                            }
                            disabled={!editMode}
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        {...requiredTextFieldProps("Email")}
                        name="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.email && Boolean(formik.errors.email)
                        }
                        helperText={formik.touched.email && formik.errors.email}
                        disabled={true}
                      />
                    </Grid>
                    {(isSuperAdminPersonalMode || isTeacherOrHOD) && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            select
                            {...requiredTextFieldProps("Gender")}
                            name="gender"
                            value={formik.values.gender || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.gender && Boolean(formik.errors.gender)}
                            helperText={formik.touched.gender && formik.errors.gender}
                            disabled={!editMode}
                            SelectProps={{ native: true }}
                          >
                            <option value="">Select Gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DatePicker
                            label="Date of Birth"
                            format={DATE_DISPLAY_FORMAT}
                            value={formik.values.dob ? new Date(String(formik.values.dob).split("T")[0]) : null}
                            onChange={(val) => {
                              formik.setFieldTouched("dob", true, false);
                              formik.setFieldValue("dob", toApiDate(val), true);
                            }}
                            disabled={!editMode}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                required: true,
                                InputLabelProps: requiredInputLabelProps,
                                error: formik.touched.dob && Boolean(formik.errors.dob),
                                helperText: formik.touched.dob && formik.errors.dob,
                              },
                            }}
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <FormControl 
                        fullWidth
                        required
                        error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                        sx={{ width: '100%' }}
                      >
                        <InputLabel shrink required sx={{ ...REQUIRED_ASTERISK_SX, position: "relative", transform: "none", mb: 0.5 }}>
                          Phone
                        </InputLabel>
                        <PhoneInput
                          country={'in'}
                          value={formik.values.phoneNumber || formik.values.phone}
                          onChange={(phone) => {
                            formik.setFieldValue('phoneNumber', phone, true);
                            formik.setFieldValue('phone', phone, false); // Keep both fields in sync
                            formik.setFieldTouched('phoneNumber', true, false);
                            formik.setFieldTouched('phone', true, false);
                          }}
                          onBlur={() => {
                            formik.setFieldTouched('phoneNumber', true, true);
                            formik.setFieldTouched('phone', true, true);
                          }}
                          disabled={!editMode}
                          inputStyle={{
                            width: '100%',
                            height: '56px',
                            paddingLeft: '50px',
                            boxSizing: 'border-box'
                          }}
                          containerStyle={{
                            width: '100%',
                            marginTop: '8px',
                            marginBottom: '8px'
                          }}
                          inputProps={{
                            name: 'phoneNumber',
                            required: true,
                            autoFocus: false
                          }}
                          countryCodeEditable={false}
                          specialLabel="Phone"
                        />
                        {(formik.touched.phoneNumber || formik.touched.phone) && 
                         (formik.errors.phoneNumber || formik.errors.phone) && (
                          <FormHelperText error>
                            {formik.errors.phoneNumber || formik.errors.phone}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    
                    {showInstituteProfile && (
                      <>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            {...requiredTextFieldProps("Street")}
                            name="street"
                            value={formik.values.street}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.street && Boolean(formik.errors.street)}
                            helperText={formik.touched.street && formik.errors.street}
                            disabled={!editMode}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth required error={formik.touched.state && Boolean(formik.errors.state)} disabled={!editMode}>
                            <InputLabel id="profile-state-label" shrink {...requiredFormControlLabelProps("State")} />
                            <Select
                              labelId="profile-state-label"
                              id="state"
                              name="state"
                              value={INDIAN_STATES.find((s) => s.toLowerCase() === (formik.values.state || "").toLowerCase()) || formik.values.state || ""}
                              label="State"
                              onBlur={formik.handleBlur}
                              onChange={(e) => {
                                const newState = e.target.value;
                                formik.setFieldValue('state', newState);
                                const stateKey = INDIAN_STATES.find((s) => s.toLowerCase() === (newState || "").toLowerCase()) || newState;
                                const citiesForState = STATE_CITIES[stateKey] || [];
                                if (formik.values.city && !citiesForState.includes(formik.values.city)) {
                                  formik.setFieldValue('city', '');
                                }
                              }}
                              displayEmpty
                              renderValue={(v) => v || "Select state"}
                            >
                              <MenuItem value="">
                                <em>Select state</em>
                              </MenuItem>
                              {INDIAN_STATES.map((state) => (
                                <MenuItem key={state} value={state}>
                                  {state}
                                </MenuItem>
                              ))}
                            </Select>
                            {formik.touched.state && formik.errors.state && (
                              <FormHelperText>{formik.errors.state}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth required error={formik.touched.city && Boolean(formik.errors.city)} disabled={!editMode}>
                            <InputLabel id="profile-city-label" shrink {...requiredFormControlLabelProps("City")} />
                            <Select
                              labelId="profile-city-label"
                              id="city"
                              name="city"
                              value={formik.values.city || ""}
                              label="City"
                              onBlur={formik.handleBlur}
                              onChange={(e) => formik.setFieldValue('city', e.target.value)}
                              displayEmpty
                              renderValue={(v) => v || (formik.values.state ? "Select city" : "Select state first")}
                            >
                              <MenuItem value="">
                                <em>{formik.values.state ? "Select city" : "Select state first"}</em>
                              </MenuItem>
                              {(() => {
                                const stateKey = INDIAN_STATES.find((s) => s.toLowerCase() === (formik.values.state || "").toLowerCase()) || formik.values.state;
                                const cities = STATE_CITIES[stateKey] ? [...STATE_CITIES[stateKey]] : [];
                                const currentCity = formik.values.city;
                                if (currentCity && !cities.includes(currentCity)) {
                                  cities.unshift(currentCity);
                                }
                                return cities.map((city) => (
                                  <MenuItem key={city} value={city}>
                                    {city}
                                  </MenuItem>
                                ));
                              })()}
                            </Select>
                            {formik.touched.city && formik.errors.city && (
                              <FormHelperText>{formik.errors.city}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            {...requiredTextFieldProps("Country")}
                            name="country"
                            value={formik.values.country}
                            onChange={handleCityStateCountryChange('country')}
                            onKeyDown={blockNumberKeyDown}
                            onBlur={formik.handleBlur}
                            error={formik.touched.country && Boolean(formik.errors.country)}
                            helperText={formik.touched.country && formik.errors.country}
                            disabled={!editMode}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            {...requiredTextFieldProps("Pin Code")}
                            name="pinCode"
                            value={formik.values.pinCode || ''}
                            onChange={(e) => {
                              // Only allow numeric input and limit to 9 digits
                              const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                              formik.setFieldValue('pinCode', value, true);
                              formik.setFieldTouched('pinCode', true, false);
                            }}
                            onBlur={formik.handleBlur}
                            error={(formik.touched.pinCode || (formik.values.pinCode?.length > 0)) && Boolean(formik.errors.pinCode)}
                            helperText={(formik.touched.pinCode || (formik.values.pinCode?.length > 0)) && formik.errors.pinCode}
                            disabled={!editMode}
                            inputProps={{
                              inputMode: 'numeric',
                              pattern: '[0-9]*',
                              minLength: 3,
                              maxLength: 9
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            {...requiredTextFieldProps("Website")}
                            name="website"
                            value={formik.values.website}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.website && Boolean(formik.errors.website)}
                            helperText={formik.touched.website && formik.errors.website}
                            disabled={!editMode}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Latitude"
                            name="latitude"
                            value={formik.values.latitude || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Allow decimal numbers for latitude
                              if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
                                formik.setFieldValue('latitude', value, true);
                              }
                            }}
                            onBlur={formik.handleBlur}
                            error={formik.touched.latitude && Boolean(formik.errors.latitude)}
                            helperText={formik.touched.latitude && formik.errors.latitude}
                            disabled={!editMode}
                            inputProps={{
                              step: 'any',
                              min: -90,
                              max: 90
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Longitude"
                            name="longitude"
                            value={formik.values.longitude || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Allow decimal numbers for longitude
                              if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
                                formik.setFieldValue('longitude', value, true);
                              }
                            }}
                            onBlur={formik.handleBlur}
                            error={formik.touched.longitude && Boolean(formik.errors.longitude)}
                            helperText={formik.touched.longitude && formik.errors.longitude}
                            disabled={!editMode}
                            inputProps={{
                              step: 'any',
                              min: -180,
                              max: 180
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Institute Distance Radius (meters)"
                            name="instituteDistanceRadius"
                            value={formik.values.instituteDistanceRadius || ''}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              formik.setFieldValue('instituteDistanceRadius', value ? Number(value) : '', true);
                            }}
                            onBlur={formik.handleBlur}
                            error={formik.touched.instituteDistanceRadius && Boolean(formik.errors.instituteDistanceRadius)}
                            helperText={formik.touched.instituteDistanceRadius ? formik.errors.instituteDistanceRadius : "Distance from institute location to add to attendance radius"}
                            disabled={!editMode}
                            inputProps={{
                              inputMode: 'numeric',
                              min: 0,
                              max: 10000
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Teacher-Class Distance Radius (meters)"
                            name="teacherClassDistanceRadius"
                            value={formik.values.teacherClassDistanceRadius || ''}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              formik.setFieldValue('teacherClassDistanceRadius', value ? Number(value) : '', true);
                            }}
                            onBlur={formik.handleBlur}
                            error={formik.touched.teacherClassDistanceRadius && Boolean(formik.errors.teacherClassDistanceRadius)}
                            helperText={formik.touched.teacherClassDistanceRadius ? formik.errors.teacherClassDistanceRadius : "Distance from teacher to class location to add to attendance radius"}
                            disabled={!editMode}
                            inputProps={{
                              inputMode: 'numeric',
                              min: 0,
                              max: 5000
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            type="number"
                            label="QR/OTP Code Refresh Interval (seconds)"
                            name="attendanceTokenRefreshSeconds"
                            placeholder="60"
                            value={
                              formik.values.attendanceTokenRefreshSeconds ??
                              ''
                            }
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.attendanceTokenRefreshSeconds &&
                              Boolean(formik.errors.attendanceTokenRefreshSeconds)
                            }
                            helperText={
                              formik.touched.attendanceTokenRefreshSeconds &&
                              formik.errors.attendanceTokenRefreshSeconds
                                ? formik.errors.attendanceTokenRefreshSeconds
                                : "Leave blank to use the system default (60 seconds). Allowed range: 15–1800 seconds."
                            }
                            disabled={!editMode}
                            inputProps={{
                              inputMode: 'numeric',
                              min: 15,
                              max: 1800,
                              step: 1
                            }}
                          />
                        </Grid>

                      </>
                    )}
                    {isSuperAdminPersonalMode && (
                      <>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Address"
                            name="address"
                            value={formik.values.address}
                            onChange={formik.handleChange}
                            disabled={!editMode}
                            multiline
                            rows={2}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label="City"
                            name="city"
                            value={formik.values.city}
                            onChange={handleCityStateCountryChange('city')}
                            onKeyDown={blockNumberKeyDown}
                            disabled={!editMode}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label="State"
                            name="state"
                            value={formik.values.state}
                            onChange={handleCityStateCountryChange('state')}
                            onKeyDown={blockNumberKeyDown}
                            disabled={!editMode}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label="Pincode"
                            name="pinCode"
                            value={formik.values.pinCode || ''}
                            onChange={formik.handleChange}
                            disabled={!editMode}
                          />
                        </Grid>
                      </>
                    )}
                    {isTeacherOrHOD && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Teacher ID"
                            name="teacherLoginID"
                            value={formik.values.teacherLoginID}
                            onChange={formik.handleChange}
                            disabled={true}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Role"
                            name="role"
                            value={formik.values.role}
                            onChange={formik.handleChange}
                            disabled={true}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Full Name"
                            name="fullName"
                            value={`${formik.values.firstName || ""} ${formik.values.lastName || ""}`.trim()}
                            onChange={formik.handleChange}
                            disabled={true}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            {...requiredTextFieldProps("Qualification")}
                            name="qualification"
                            value={formik.values.qualification}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.qualification && Boolean(formik.errors.qualification)}
                            helperText={formik.touched.qualification && formik.errors.qualification}
                            disabled={!editMode}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            {...requiredTextFieldProps("Experience (Years)")}
                            name="experienceYears"
                            value={formik.values.experienceYears}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                              formik.setFieldValue('experienceYears', value, true);
                            }}
                            onBlur={formik.handleBlur}
                            error={formik.touched.experienceYears && Boolean(formik.errors.experienceYears)}
                            helperText={formik.touched.experienceYears && formik.errors.experienceYears}
                            disabled={!editMode}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <DatePicker
                            label="Joining Date"
                            format={DATE_DISPLAY_FORMAT}
                            value={formik.values.joiningDate ? new Date(String(formik.values.joiningDate).split("T")[0]) : null}
                            onChange={(val) => {
                              formik.setFieldTouched("joiningDate", true, false);
                              formik.setFieldValue("joiningDate", toApiDate(val), true);
                            }}
                            disabled={!editMode}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                required: true,
                                InputLabelProps: requiredInputLabelProps,
                                error: formik.touched.joiningDate && Boolean(formik.errors.joiningDate),
                                helperText: formik.touched.joiningDate && formik.errors.joiningDate,
                              },
                            }}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            {...requiredTextFieldProps("Designation")}
                            name="designation"
                            value={formik.values.designation}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.designation && Boolean(formik.errors.designation)}
                            helperText={formik.touched.designation && formik.errors.designation}
                            disabled={!editMode}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            {...requiredTextFieldProps("Department Name")}
                            name="departmentName"
                            value={formik.values.departmentName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.departmentName && Boolean(formik.errors.departmentName)}
                            helperText={formik.touched.departmentName && formik.errors.departmentName}
                            disabled={!editMode}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            {...requiredTextFieldProps("Salary")}
                            name="salary"
                            value={formik.values.salary}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^\d.]/g, '');
                              const normalized = value
                                .replace(/(\..*)\./g, '$1')
                                .replace(/^(\d+)\.(\d{0,2}).*$/, '$1.$2');
                              formik.setFieldValue('salary', normalized, true);
                            }}
                            onBlur={formik.handleBlur}
                            error={formik.touched.salary && Boolean(formik.errors.salary)}
                            helperText={formik.touched.salary && formik.errors.salary}
                            disabled={!editMode}
                            inputProps={{ inputMode: 'decimal' }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Institute Name"
                            name="instituteName"
                            value={formik.values.instituteName}
                            onChange={formik.handleChange}
                            disabled={true}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            {...requiredTextFieldProps("Street")}
                            name="street"
                            value={formik.values.street}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.street && Boolean(formik.errors.street)}
                            helperText={formik.touched.street && formik.errors.street}
                            disabled={!editMode}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            {...requiredTextFieldProps("City")}
                            name="city"
                            value={formik.values.city}
                            onChange={handleCityStateCountryChange('city')}
                            onKeyDown={blockNumberKeyDown}
                            onBlur={formik.handleBlur}
                            error={formik.touched.city && Boolean(formik.errors.city)}
                            helperText={formik.touched.city && formik.errors.city}
                            disabled={!editMode}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            {...requiredTextFieldProps("State")}
                            name="state"
                            value={formik.values.state}
                            onChange={handleCityStateCountryChange('state')}
                            onKeyDown={blockNumberKeyDown}
                            onBlur={formik.handleBlur}
                            error={formik.touched.state && Boolean(formik.errors.state)}
                            helperText={formik.touched.state && formik.errors.state}
                            disabled={!editMode}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            {...requiredTextFieldProps("Country")}
                            name="country"
                            value={formik.values.country}
                            onChange={handleCityStateCountryChange('country')}
                            onKeyDown={blockNumberKeyDown}
                            onBlur={formik.handleBlur}
                            error={formik.touched.country && Boolean(formik.errors.country)}
                            helperText={formik.touched.country && formik.errors.country}
                            disabled={!editMode}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            {...requiredTextFieldProps("Pincode")}
                            name="pinCode"
                            value={formik.values.pinCode}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                              formik.setFieldValue('pinCode', value, true);
                            }}
                            onBlur={formik.handleBlur}
                            error={formik.touched.pinCode && Boolean(formik.errors.pinCode)}
                            helperText={formik.touched.pinCode && formik.errors.pinCode}
                            disabled={!editMode}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 9 }}
                          />
                        </Grid>

                      </>
                    )}
                  </Grid>

                  {/* Contact Info quick view (approval workflow — not for Super Admin personal profile) */}
                  {!isSuperAdminPersonalMode && (
                  <Box sx={{ mt: 3 }}>
                    <Card variant="outlined" sx={{ bgcolor: "grey.50" }}>
                      <CardContent sx={{ py: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              Contact Info
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Phone, email, address, and emergency contact — managed separately from profile edit permission.
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            onClick={() => navigate("/profile/contact-requests")}
                          >
                            Contact Info Requests
                          </Button>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        {isContactLoading ? (
                          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                            <CircularProgress size={18} />
                          </Box>
                        ) : (
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">
                                Email
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {profileContact.email || "Not provided"}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">
                                Phone
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {profileContact.phone || "Not provided"}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">
                                Address
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {profileContact.address || "Not provided"}
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">
                                Emergency
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {profileContact.emergency || "Not provided"}
                              </Typography>
                            </Grid>
                          </Grid>
                        )}
                      </CardContent>
                    </Card>
                  </Box>
                  )}

                  {!editMode && !isSuperAdmin && !isInstitute && !isAdmin && !canEditProfile && isTeacherOrHOD && (
                    <Box
                      sx={{ mt: 3, p: 2, bgcolor: "grey.50", borderRadius: 2 }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Use <strong>Request To Edit</strong> above to ask for profile editing permission, then track it via{" "}
                        <strong>View Profile Edit Requests</strong>.
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        For phone, email, or address changes, use <strong>Request Contact Edit</strong> on the Contact Info Requests page in the sidebar.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {activeTab === 1 && (
                <Box component="form" onSubmit={passwordFormik.handleSubmit}>
                  <Typography variant="h6" gutterBottom>
                    Change Password
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Ensure your account is using a strong password for security.
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Current Password *"
                        name="currentPassword"
                        type={showPasswords.current ? "text" : "password"}
                        value={passwordFormik.values.currentPassword}
                        onChange={passwordFormik.handleChange}
                        onBlur={passwordFormik.handleBlur}
                        error={
                          passwordFormik.touched.currentPassword &&
                          Boolean(passwordFormik.errors.currentPassword)
                        }
                        helperText={
                          passwordFormik.touched.currentPassword &&
                          passwordFormik.errors.currentPassword
                        }
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              onClick={() =>
                                togglePasswordVisibility("current")
                              }
                              edge="end"
                            >
                              {showPasswords.current ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="New Password *"
                        name="newPassword"
                        type={showPasswords.new ? "text" : "password"}
                        value={passwordFormik.values.newPassword}
                        onChange={passwordFormik.handleChange}
                        onBlur={passwordFormik.handleBlur}
                        error={
                          passwordFormik.touched.newPassword &&
                          Boolean(passwordFormik.errors.newPassword)
                        }
                        helperText={
                          passwordFormik.touched.newPassword &&
                          passwordFormik.errors.newPassword
                        }
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              onClick={() => togglePasswordVisibility("new")}
                              edge="end"
                            >
                              {showPasswords.new ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Confirm New Password *"
                        name="confirmNewPassword"
                        type={showPasswords.confirm ? "text" : "password"}
                        value={passwordFormik.values.confirmNewPassword}
                        onChange={passwordFormik.handleChange}
                        onBlur={passwordFormik.handleBlur}
                        error={
                          passwordFormik.touched.confirmNewPassword &&
                          Boolean(passwordFormik.errors.confirmNewPassword)
                        }
                        helperText={
                          passwordFormik.touched.confirmNewPassword &&
                          passwordFormik.errors.confirmNewPassword
                        }
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              onClick={() =>
                                togglePasswordVisibility("confirm")
                              }
                              edge="end"
                            >
                              {showPasswords.confirm ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={
                          changePasswordMutation.isPending ||
                          changePasswordMutation.isLoading
                        }
                      >
                        {(changePasswordMutation.isPending ||
                          changePasswordMutation.isLoading) && (
                          <CircularProgress
                            size={18}
                            color="inherit"
                            sx={{ mr: 1 }}
                          />
                        )}
                        Update Password
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {activeTab === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Notification Preferences
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Control whether you receive mobile push notifications from the ERP app.
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={pushNotificationsEnabled}
                        onChange={(e) => {
                          const next = Boolean(e.target.checked);
                          updateNotificationSettingsMutation.mutate({ pushNotificationsEnabled: next });
                        }}
                        disabled={isNotificationSettingsLoading || updateNotificationSettingsMutation.isPending}
                      />
                    }
                    label="Push Notifications"
                    {...requiredFormControlLabelProps}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <ConfirmDialog
        open={removePhotoConfirmOpen}
        title="Remove profile photo?"
        message="Your profile photo will be removed. Your initials will be shown instead."
        severity="error"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        loading={removeProfileImageMutation.isPending}
        onClose={() => setRemovePhotoConfirmOpen(false)}
        onConfirm={() => removeProfileImageMutation.mutate()}
      />
      {/* Top-right toast for change password response */}
      <Toast
        open={!!toastMessage}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </Box>
  );
};

export default Profile;
